import type { ActionKind } from './types.ts';

/**
 * The words this module uses, in one place.
 *
 * Every map here mirrors a closed set the API also holds — a z.enum in
 * @dayon/contracts, or a column's documented values. Two copies of a closed set
 * is a real risk and the alternative is worse: fetching the label for a reason
 * code would make a write dialog wait on a round trip to render a <select>.
 * They are asserted against each other in the tests instead.
 */

/** "Active = accented, Invited = outline, anything else quiet." */
export const STATUS: Record<
  string,
  { label: string; tone: 'accent' | 'neutral' | 'outline' | 'quiet' }
> = {
  ACTIVE: { label: 'Active', tone: 'accent' },
  INVITED: { label: 'Invited', tone: 'outline' },
  SUSPENDED: { label: 'Suspended', tone: 'quiet' },
};

/**
 * The roster's 2FA column, three states.
 *
 * Never colour alone: the WORDS carry the distinction, which is why "Pending
 * setup" is spelled out rather than being a differently-shaded "None". An admin
 * mid-enrolment and an admin who has never started are different facts, and
 * only one of them is a problem a security lead has to chase.
 */
export const TWO_FACTOR: Record<
  string,
  { label: string; tone: 'accent' | 'neutral' | 'outline' | 'quiet'; detail: string }
> = {
  App: { label: 'App', tone: 'neutral', detail: 'Authenticator app' },
  'Pending setup': {
    label: 'Pending setup',
    tone: 'quiet',
    detail: 'Enrolment started, no code confirmed yet',
  },
  None: { label: 'None', tone: 'outline', detail: 'Not enrolled' },
};

/** Whether this 2FA state actually satisfies a factor. Only one of them does. */
export const hasFactor = (twoFactor: string): boolean => twoFactor === 'App';

/** auth_attempt.outcome, in operator language. Every one is paired with words,
    never a colour on its own. */
export const OUTCOME: Record<string, { label: string; tone: 'neutral' | 'outline' | 'quiet' }> = {
  SUCCESS: { label: 'Success', tone: 'neutral' },
  BAD_PASSWORD: { label: 'Failed', tone: 'outline' },
  BAD_TOTP: { label: 'Failed', tone: 'outline' },
  NO_SUCH_USER: { label: 'Failed', tone: 'outline' },
  LOCKED: { label: 'Locked out', tone: 'quiet' },
  SUSPENDED: { label: 'Suspended', tone: 'quiet' },
};

/** The "Detail" cell's first clause: which factor the attempt died on. */
export const STAGE: Record<string, string> = {
  PASSWORD: 'Password',
  TOTP: '2FA app',
  RECOVERY_CODE: 'Recovery code',
  DEVICE_TRUST: 'Trusted device',
};

export const OUTCOME_DETAIL: Record<string, string> = {
  SUCCESS: 'accepted',
  BAD_PASSWORD: 'wrong password',
  BAD_TOTP: 'wrong code',
  NO_SUCH_USER: 'no such admin',
  LOCKED: 'account locked',
  SUSPENDED: 'account suspended',
};

/**
 * platform.audit_log.action, in operator language.
 *
 * The log is written by every module, so this covers what THIS module writes
 * plus the actions an admin's recent-actions list is most likely to contain.
 * Anything unmapped is said as words by `codeAsWords` (lib/audit-words.ts)
 * rather than a shrug, and never printed as its code — an action nobody has
 * written a label for is still a fact about what somebody did.
 */
export const AUDIT_ACTION: Record<string, string> = {
  SIGN_IN: 'Signed in',
  ADMIN_INVITED: 'Invited an admin',
  ADMIN_INVITE_LINK_ISSUED: 'Issued an invite link',
  ADMIN_INVITE_ACCEPTED: 'Accepted their invite',
  ADMIN_ROLE_CHANGED: 'Changed an admin’s role',
  ADMIN_SUSPENDED: 'Suspended an admin',
  ADMIN_REINSTATED: 'Reinstated an admin',
  ADMIN_BRAND_SCOPE_CHANGED: 'Changed an admin’s brand scope',
  ADMIN_TWO_FACTOR_RESET: 'Reset an admin’s second factor',
  ADMIN_TWO_FACTOR_ENROLLED: 'Enrolled an authenticator',
  ADMIN_SESSIONS_ENDED: 'Ended an admin’s sessions',
  /* Self-service recovery, from the sign-in screen. Worded so the trail
     reads as the two separate events they are: asking for a link leaves a
     row even when the link is never used, which is the row an
     investigation into an unexpected reset starts from. */
  ADMIN_PASSWORD_RESET_REQUESTED: 'Asked for a password-reset link',
  ADMIN_PASSWORD_RESET_COMPLETED: 'Set a new password from a reset link',
  ROLE_PERMISSION_GRANTED: 'Granted a capability to a role',
  ROLE_PERMISSION_REVOKED: 'Took a capability from a role',
  AUDIT_LOG_EXPORTED: 'Exported the audit trail',
  PLAYER_ACTIVITY_EXPORTED: 'Exported a player’s activity log',
  'report.run': 'Ran a report',
  'report.download': 'Downloaded a report',
  'report.schedule.create': 'Scheduled a report',
  'report.schedule.pause': 'Paused a report schedule',
  'report.schedule.resume': 'Resumed a report schedule',
};

/** A note shorter than this is a form number, not a reason. Matches the contract. */
export const NOTE_MINIMUM = 10;

/**
 * The reason codes each write accepts, in operator language.
 *
 * Closed sets, mirroring the z.enum in @dayon/contracts: an access review
 * groups on the CODE — "how many role changes were offboarding steps?" — and
 * prose cannot be grouped. The label is what the operator picks; the code is
 * what is stored and what the audit row carries.
 */
export const REASONS: Record<ActionKind, readonly { code: string; label: string }[]> = {
  role: [
    { code: 'PROMOTION', label: 'Promotion' },
    { code: 'ROLE_REALIGNMENT', label: 'Role realignment' },
    { code: 'TEMPORARY_COVER', label: 'Temporary cover' },
    { code: 'SEGREGATION_OF_DUTIES', label: 'Segregation of duties' },
    { code: 'OFFBOARDING_STEP', label: 'Offboarding step' },
  ],
  suspend: [
    { code: 'OFFBOARDING', label: 'Offboarding' },
    { code: 'SUSPECTED_COMPROMISE', label: 'Suspected account compromise' },
    { code: 'POLICY_BREACH', label: 'Policy breach' },
    { code: 'INVESTIGATION', label: 'Under investigation' },
    { code: 'EXTENDED_LEAVE', label: 'Extended leave' },
  ],
  reinstate: [
    { code: 'INVESTIGATION_CLEARED', label: 'Investigation cleared' },
    { code: 'RETURNED_FROM_LEAVE', label: 'Returned from leave' },
    { code: 'SUSPENDED_IN_ERROR', label: 'Suspended in error' },
    { code: 'ACCESS_RESTORED', label: 'Access restored' },
  ],
  scope: [
    { code: 'ROLE_REALIGNMENT', label: 'Role realignment' },
    { code: 'BRAND_LAUNCH', label: 'Brand launch' },
    { code: 'BRAND_HANDOVER', label: 'Brand handover' },
    { code: 'ACCESS_REVIEW', label: 'Access review' },
    { code: 'SEGREGATION_OF_DUTIES', label: 'Segregation of duties' },
    { code: 'GRANTED_IN_ERROR', label: 'Granted in error' },
  ],
  reset2fa: [
    { code: 'DEVICE_LOST', label: 'Device lost' },
    { code: 'DEVICE_REPLACED', label: 'Device replaced' },
    { code: 'SUSPECTED_COMPROMISE', label: 'Suspected account compromise' },
    { code: 'ENROLMENT_ERROR', label: 'Enrolment went wrong' },
    { code: 'RETURNING_FROM_LEAVE', label: 'Returning from leave' },
  ],
  signout: [
    { code: 'DEVICE_LOST', label: 'Device lost' },
    { code: 'SUSPECTED_COMPROMISE', label: 'Suspected account compromise' },
    { code: 'SHARED_CREDENTIAL', label: 'Credential was shared' },
    { code: 'ROLE_CHANGE_HYGIENE', label: 'Hygiene after a role change' },
    { code: 'AT_THEIR_REQUEST', label: 'At their own request' },
  ],
};

/** The reason codes a matrix cell toggle accepts. Its own set: an access review
    counts "who widened a role and why" separately from who moved a person. */
export const MATRIX_REASONS: readonly { code: string; label: string }[] = [
  { code: 'ACCESS_REVIEW', label: 'Access review' },
  { code: 'ROLE_REALIGNMENT', label: 'Role realignment' },
  { code: 'SEGREGATION_OF_DUTIES', label: 'Segregation of duties' },
  { code: 'INCIDENT_RESPONSE', label: 'Incident response' },
  { code: 'GRANTED_IN_ERROR', label: 'Granted in error' },
  { code: 'NEW_RESPONSIBILITY', label: 'New responsibility' },
];

export const ACTION_TITLE: Record<ActionKind, string> = {
  role: 'Change role',
  suspend: 'Suspend account',
  reinstate: 'Reinstate account',
  scope: 'Brand scope',
  reset2fa: 'Reset 2FA',
  signout: 'Sign out all sessions',
};

/**
 * How an admin's session ended, in words — one for every reason the API writes when it ends one
 * (held to them by session-end-words.test.tsx). The drawer used to print "revoked: " and the stored
 * reason in lower case.
 */
export const ADMIN_SESSION_ENDED_BY: Readonly<Record<string, string>> = {
  SIGNED_OUT: 'ended when they signed out',
  SIGNED_OUT_EVERYWHERE: 'ended when they signed out everywhere',
  PASSWORD_CHANGED: 'ended when they changed their password',
  PASSWORD_RESET: 'ended by a password reset',
  TWO_FACTOR_REPLACED: 'ended when they replaced their authenticator',
  TWO_FACTOR_RESET: 'ended when their 2FA was reset',
  ADMIN_SIGNED_OUT: 'ended by an administrator',
  ADMIN_SUSPENDED: 'ended when the account was suspended',
};

/** The tail of the route each action posts to. */
export const ACTION_PATH: Record<ActionKind, string> = {
  role: 'role',
  suspend: 'suspend',
  reinstate: 'reinstate',
  scope: 'brand-scope',
  reset2fa: 'reset-2fa',
  signout: 'sign-out',
};
